import client from "@/api/openapi-ts";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref, computed } from "vue";
import { usePlugins } from "./helpers/usePlugins";
import { useUserStore } from "@/stores/UserStore";
import { useErrorStore } from "@/stores/ErrorStore";
import { usePermissions } from "@/composables/usePermissions";
import { featureFlags } from "@/lib/feature_flags/flags";
import { useExports } from "./helpers/useExports";
import type { components } from "@/api/schema";
import type { Geography } from "@/types/api/Geography";

export const BASE_MEDIUM_TYPES = ["Broadcast", "Cable"] as const;
export const CTV_MEDIUM_TYPES = [
  "Broadcast",
  "Cable",
  "Streaming",
  "YouTube",
] as const;

type schemas = components["schemas"];

/**
 * The `reach_frequency/*` endpoints (dates, categories, races, sponsors,
 * network_stations, creatives) are plain Flask routes without flask-smorest
 * schemas, so they are untyped in the generated OpenAPI client. Their request
 * bodies/responses are cast at the call sites until the backend gains schemas.
 */
type PendingTaskResponse = {
  task_id: string;
};

type Selections = {
  compareTo?: number;
  frequency?: number;
};

export type Dates = {
  minDate?: string;
  maxDate?: string;
};

export type Category = {
  id: number;
  name: string;
};

export type Race = string;

export type Sponsor = {
  id: number;
  name: string;
};

export type Creative = {
  id: number;
  ids: number[];
  title: string;
};

export type Filters = {
  meta_audience_id?: number;
  date_ranges?: string[];
  category?: Category;
  races?: string;
  advertiser_parties?: string[];
  geographies?: Geography[];
  ad_types?: string[];
  medium_types?: string[];
  sponsors?: Sponsor[];
  creatives?: Creative[];
  network_stations?: string[];
  creative_ids?: number[];
  impression_ranges?: number[][];
  impression_ranges_by_market?: {
    ref_id: number;
    impression_ranges: number[][];
  }[];
  exposure_ranges?: number[][];
  market_exposure_ranges?: number[][];
};

type Loading = {
  dateRange: boolean;
  categories: boolean;
  races: boolean;
  sponsors: boolean;
  networkStations: boolean;
  creatives: boolean;
  reachMetrics: boolean;
  frequencyMetrics: boolean;
  mostEffectiveMedium: boolean;
  audienceSize: boolean;
  marketAudienceSize: boolean;
  exposureOverview: boolean;
  marketBreakdown: boolean;
  additionalMarketBreakdown: boolean;
  reachByDay: boolean;
  creativesBreakdown: boolean;
};

type Errors = {
  reachMetrics: string | undefined;
  frequencyMetrics: string | undefined;
  mostEffectiveMedium: string | undefined;
  exposureOverview: string | undefined;
  marketBreakdown: string | undefined;
  reachByDay: string | undefined;
  creativesBreakdown: string | undefined;
  audienceSize: string | undefined;
  marketAudienceSize: string | undefined;
};

/** Overview vs market breakdown exposure slices (matches store refs / `loading` keys). */
export type ExposureFrequencyType = keyof Pick<
  Loading,
  "exposureOverview" | "marketBreakdown"
>;

type TaskIds = {
  dateRange: string | undefined;
  categories: string | undefined;
  races: string | undefined;
  sponsors: string | undefined;
  networkStations: string | undefined;
  creatives: string | undefined;
  reachMetrics: string | undefined;
  frequencyMetrics: string | undefined;
  mostEffectiveMedium: string | undefined;
  audienceSize: string | undefined;
  marketAudienceSize: string | undefined;
  exposureOverview: string | undefined;
  marketBreakdown: string | undefined;
  reachByDay: string | undefined;
  creativesBreakdown: string | undefined;
};

type ExposureMarkets = {
  total: number;
  markets: Geography[];
  page_size: number;
  page_number: number;
};

export const useReachFreqStore = defineStore("reachFreq", () => {
  const userStore = useUserStore();
  const { hasRole } = usePermissions();

  const loading = ref<Loading>({
    dateRange: false,
    categories: false,
    races: false,
    sponsors: false,
    networkStations: false,
    creatives: false,
    reachMetrics: false,
    frequencyMetrics: false,
    mostEffectiveMedium: false,
    audienceSize: false,
    marketAudienceSize: false,
    exposureOverview: false,
    marketBreakdown: false,
    additionalMarketBreakdown: false,
    reachByDay: false,
    creativesBreakdown: false,
  });
  const errors = ref<Errors>({
    reachMetrics: undefined,
    frequencyMetrics: undefined,
    mostEffectiveMedium: undefined,
    exposureOverview: undefined,
    marketBreakdown: undefined,
    reachByDay: undefined,
    creativesBreakdown: undefined,
    audienceSize: undefined,
    marketAudienceSize: undefined,
  });
  const taskIds = ref<TaskIds>({
    dateRange: undefined,
    categories: undefined,
    races: undefined,
    sponsors: undefined,
    networkStations: undefined,
    creatives: undefined,
    reachMetrics: undefined,
    frequencyMetrics: undefined,
    mostEffectiveMedium: undefined,
    audienceSize: undefined,
    marketAudienceSize: undefined,
    exposureOverview: undefined,
    marketBreakdown: undefined,
    reachByDay: undefined,
    creativesBreakdown: undefined,
  });
  const reachMetrics =
    ref<components["schemas"]["ReachFrequencyGetReachResponse"]>();
  const frequencyMetrics =
    ref<components["schemas"]["ReachFrequencyGetFrequencyResponse"]>();
  const mostEffectiveMedium =
    ref<
      components["schemas"]["ReachFrequencyGetMostEffectiveMediumResponse"]
    >();
  const audienceSize =
    ref<components["schemas"]["AudienceBuilderCountsResponse"]>();
  const marketAudienceSize =
    ref<components["schemas"]["AudienceBuilderCountsResponse"]>();
  const exposureMarkets = ref<ExposureMarkets>();
  const exposureOverview =
    ref<components["schemas"]["ReachFrequencyGetExposureOverviewResponse"]>();
  /** WebSocket assigns `payload.data.results`, not the full API envelope. */
  const marketBreakdown =
    ref<
      components["schemas"]["ReachFrequencyGetExposureMarketBreakdownResult"][]
    >();
  const reachByDay =
    ref<
      components["schemas"]["ReachFrequencyGetReachAndFrequencyByDayResponse"]
    >();
  /** WebSocket sets `payload.data.creatives` (array), not the full API envelope. */
  const creativesBreakdown =
    ref<
      components["schemas"]["ReachFrequencyGetCreativesBreakdownCreative"][]
    >();
  const lineGraphExtremes = ref<{
    reach: { min: number; max: number; ticks: number[] };
    frequency: { min: number; max: number; ticks: number[] };
  }>({
    reach: {
      min: 0,
      max: 0,
      ticks: [],
    },
    frequency: {
      min: 0,
      max: 0,
      ticks: [],
    },
  });
  const selections = ref<Selections>({
    compareTo: 7,
    frequency: 1,
  });

  const loadingMetrics = computed(
    () => loading.value.reachMetrics || loading.value.frequencyMetrics,
  );
  const loadingAllCharts = computed(
    () =>
      loading.value.reachMetrics ||
      loading.value.frequencyMetrics ||
      loading.value.mostEffectiveMedium ||
      loading.value.exposureOverview ||
      loading.value.marketBreakdown ||
      loading.value.reachByDay ||
      loading.value.audienceSize,
  );
  const exportTaskId = ref<string>();
  const hasMetrics = computed(
    () => reachMetrics.value && frequencyMetrics.value,
  );
  const dates = ref<Dates | undefined>(undefined);
  const filters = ref<Filters>({
    medium_types: [],
    ad_types: [],
  });

  const ctvDataEnabled = ref(false);
  let ctvDataFlagPromise: Promise<void> | undefined;

  const defaultMediumTypes = computed(() =>
    ctvDataEnabled.value ? [...CTV_MEDIUM_TYPES] : [...BASE_MEDIUM_TYPES],
  );

  const effectiveMediumTypes = computed(() =>
    filters.value.medium_types?.length
      ? filters.value.medium_types
      : defaultMediumTypes.value,
  );

  async function initCtvDataFlag() {
    if (!ctvDataFlagPromise) {
      ctvDataFlagPromise = featureFlags
        .flagIsEnabled("use_ctv_data", {
          email: userStore.currentUser?.email,
          admin: hasRole("admin"),
        })
        .then((enabled) => {
          ctvDataEnabled.value = enabled;
        });
    }
    return ctvDataFlagPromise;
  }

  const baseParams = (updateGeoName = false) => {
    return {
      meta_audience_id: filters.value.meta_audience_id,
      start_date: filters.value.date_ranges![0],
      end_date: filters.value.date_ranges![1],
      ...(!updateGeoName && { geographies: filters.value.geographies || [] }),
      ...(updateGeoName && {
        geographies: filters.value.geographies?.map((g) => {
          return {
            ...g,
            name: `Media Market: ${g.name}`,
          };
        }),
      }),
      category_ids: filters.value.category?.id
        ? [filters.value.category?.id]
        : [],
      races: filters.value.races ? [filters.value.races] : [],
      advertiser_parties: filters.value.advertiser_parties
        ? filters.value.advertiser_parties
        : [],
      sponsor_ids: filters.value.sponsors?.map((s) => s.id) || [],
      network_stations: filters.value.network_stations || [],
      creative_ids: filters.value.creative_ids || [],
      ad_types: filters.value.ad_types || [],
      medium_types: effectiveMediumTypes.value,
    };
  };

  const reachFreqData = computed(
    () =>
      ({
        ...baseParams(),
        exposure_ranges: filters.value.exposure_ranges || [],
        market_exposure_ranges: filters.value.market_exposure_ranges || [],
        impression_ranges: filters.value.impression_ranges || [],
        impression_ranges_by_market:
          filters.value.impression_ranges_by_market || [],
      }) as unknown as components["schemas"]["AudienceBuilderReachFrequencyData"],
  );

  const currentFilters = ref<Filters>({});
  // needed for clearing creatives table
  const initialSponsors = ref<Sponsor[] | undefined>(undefined);
  const categories = ref<Category[]>([]);
  const categoriesSorted = computed(() => {
    return [...categories.value].sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  });
  const races = ref<Race[]>([]);
  const sponsors = ref<Sponsor[]>([]);
  const networkStations = ref<string[]>([]);
  const creatives = ref<Creative[]>([]);
  const resetting = ref(false);

  const { $ws } = usePlugins();
  const errorStore = useErrorStore();

  // Shared export workflow: owns the export's loading/task state, websocket
  // subscription, and the file download once the task completes.
  const exportManager = useExports<"export">({
    channel: "ReachAndFrequency",
    keys: ["export"],
    errorMessage: () => errorStore.defaultErrorMessage,
    // Surface failures through the shared error dialog so the loader does not
    // just disappear on PPT export/download errors with no user feedback.
    onError: (_key, message) => {
      errorStore.showError(message);
    },
    fetchFile: async (file, signal) => {
      const { data } = await client.GET(
        "/api/v1/reach_frequency/export/{filename}",
        {
          params: { path: { filename: file.filename } },
          parseAs: "arrayBuffer",
          signal,
        },
      );
      return data as ArrayBuffer | undefined;
    },
  });
  const loadingExport = computed(() => exportManager.loading.export);
  const exportError = computed(() => exportManager.errors.export);

  return {
    loading,
    errors,
    loadingMetrics,
    loadingAllCharts,
    taskIds,
    reachMetrics,
    frequencyMetrics,
    mostEffectiveMedium,
    exposureMarkets,
    exposureOverview,
    audienceSize,
    marketAudienceSize,
    marketBreakdown,
    reachByDay,
    creativesBreakdown,
    lineGraphExtremes,
    hasMetrics,
    ctvDataEnabled,
    defaultMediumTypes,
    effectiveMediumTypes,
    initCtvDataFlag,
    reachFreqData,
    selections,
    filters,
    currentFilters,
    initialSponsors,
    dates,
    categories,
    categoriesSorted,
    races,
    sponsors,
    creatives,
    networkStations,
    resetting,
    loadingExport,
    exportError,
    exportTaskId,
    async getDateRange() {
      loading.value.dateRange = true;
      try {
        const { data } = await client.GET("/api/v1/reach_frequency/dates");
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.dateRange = taskId;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: taskId },
          });
        } else {
          loading.value.dateRange = false;
        }
      } catch {
        loading.value.dateRange = false;
      }
    },
    async getCategories() {
      loading.value.categories = true;
      try {
        const { data } = await client.GET("/api/v1/reach_frequency/categories");
        categories.value = (data as Category[] | undefined) ?? [];
      } finally {
        loading.value.categories = false;
      }
    },
    async getRaces() {
      loading.value.races = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST("/api/v1/reach_frequency/races", {
          body: {
            start_date: dates[0],
            end_date: dates[1],
            geographies: filters.value.geographies,
            advertiser_parties: filters.value.advertiser_parties
              ? filters.value.advertiser_parties
              : [],
          } as never,
        });
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.races = taskId;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: taskId },
          });
        } else {
          loading.value.races = false;
        }
      } catch {
        loading.value.races = false;
      }
    },
    async getSponsors() {
      const metaAudienceId = filters.value.meta_audience_id;
      if (typeof metaAudienceId !== "number") {
        return;
      }
      loading.value.sponsors = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST("/api/v1/reach_frequency/sponsors", {
          body: {
            start_date: dates[0],
            end_date: dates[1],
            meta_audience_id: metaAudienceId,
            geographies: filters.value.geographies,
            category_ids: filters.value.category?.id
              ? [filters.value.category?.id]
              : [],
            races: filters.value.races ? [filters.value.races] : [],
            ad_types: filters.value.ad_types || [],
            medium_types: effectiveMediumTypes.value,
            advertiser_parties: filters.value.advertiser_parties
              ? filters.value.advertiser_parties
              : [],
          } as never,
        });
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.sponsors = taskId;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: taskId },
          });
        } else {
          loading.value.sponsors = false;
        }
      } catch {
        loading.value.sponsors = false;
      }
    },
    async getNetworkStations() {
      loading.value.networkStations = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/network_stations",
          {
            body: {
              start_date: dates[0],
              end_date: dates[1],
              geographies: filters.value.geographies,
              category_ids: filters.value.category?.id
                ? [filters.value.category?.id]
                : [],
              races: filters.value.races ? [filters.value.races] : [],
              ad_types: filters.value.ad_types || [],
              medium_types: effectiveMediumTypes.value,
              sponsor_ids: filters.value.sponsors?.map((s) => s.id) || [],
              advertiser_parties: filters.value.advertiser_parties
                ? filters.value.advertiser_parties
                : [],
            } as never,
          },
        );
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.networkStations = taskId;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: taskId },
          });
        } else {
          loading.value.networkStations = false;
        }
      } catch {
        loading.value.networkStations = false;
      }
    },
    async getCreatives() {
      loading.value.creatives = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/creatives",
          {
            body: {
              start_date: dates[0],
              end_date: dates[1],
              geographies: filters.value.geographies,
              category_ids: filters.value.category?.id
                ? [filters.value.category?.id]
                : [],
              races: filters.value.races ? [filters.value.races] : [],
              advertiser_parties: filters.value.advertiser_parties
                ? filters.value.advertiser_parties
                : [],
              sponsor_ids: filters.value.sponsors?.map((s) => s.id) || [],
              ad_types: filters.value.ad_types || [],
              medium_types: effectiveMediumTypes.value,
              network_stations: filters.value.network_stations,
            } as never,
          },
        );
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.creatives = taskId;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: taskId },
          });
        } else {
          loading.value.creatives = false;
        }
      } catch {
        loading.value.creatives = false;
      }
    },
    async getReachMetrics() {
      loading.value.reachMetrics = true;
      errors.value.reachMetrics = undefined;
      reachMetrics.value = undefined;
      try {
        const { data } = await client.POST("/api/v1/reach_frequency/reach", {
          body: {
            ...baseParams(),
            previous_days: selections.value.compareTo,
            impression_ranges: filters.value.impression_ranges,
          } as schemas["ReachFrequencyGetReachRequest"],
        });
        if (data?.task_id) {
          taskIds.value.reachMetrics = data.task_id;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data.task_id },
          });
        } else {
          loading.value.reachMetrics = false;
        }
      } catch {
        loading.value.reachMetrics = false;
      }
    },
    async getFrequencyMetrics() {
      loading.value.frequencyMetrics = true;
      errors.value.frequencyMetrics = undefined;
      frequencyMetrics.value = undefined;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/frequency_and_impressions",
          {
            body: {
              ...baseParams(),
              previous_days: selections.value.compareTo,
              impression_ranges: filters.value.impression_ranges,
            } as schemas["ReachFrequencyGetFrequencyRequest"],
          },
        );
        if (data?.task_id) {
          taskIds.value.frequencyMetrics = data.task_id;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data.task_id },
          });
        } else {
          loading.value.frequencyMetrics = false;
        }
      } catch {
        loading.value.frequencyMetrics = false;
      }
    },
    async getMostEffectiveMedium() {
      loading.value.mostEffectiveMedium = true;
      errors.value.mostEffectiveMedium = undefined;
      mostEffectiveMedium.value = undefined;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/most_effective_medium",
          {
            body: {
              ...baseParams(),
            } as schemas["ReachFrequencyGetMostEffectiveMediumRequest"],
          },
        );
        if (data?.task_id) {
          taskIds.value.mostEffectiveMedium = data.task_id;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data.task_id },
          });
        } else {
          loading.value.mostEffectiveMedium = false;
        }
      } catch {
        loading.value.mostEffectiveMedium = false;
      }
    },
    async getAudienceSize(marketBreakdown = false) {
      const key = marketBreakdown ? "marketAudienceSize" : "audienceSize";
      loading.value[key] = true;
      errors.value[key] = undefined;
      // exclude exposure_ranges and market_exposure_ranges from the reach_frequency_data
      const {
        exposure_ranges: _exposureRanges,
        market_exposure_ranges: _marketExposureRanges,
        impression_ranges,
        impression_ranges_by_market,
        ...rest
      } = {
        ...reachFreqData.value,
      } as components["schemas"]["AudienceBuilderReachFrequencyData"] & {
        exposure_ranges: unknown;
        market_exposure_ranges: unknown;
        impression_ranges: unknown;
        impression_ranges_by_market: unknown;
      };
      const reach_frequency_data = marketBreakdown
        ? { ...rest, impression_ranges_by_market }
        : { ...rest, impression_ranges };
      if (marketBreakdown) {
        marketAudienceSize.value = undefined;
      } else {
        audienceSize.value = undefined;
      }
      try {
        const { data } = await client.POST("/api/v1/audience_builder/counts", {
          body: {
            reach_frequency_data,
          } as schemas["AudienceBuilderCountsRequest"],
        });
        taskIds.value[key] = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data?.task_id },
          });
        }
      } catch {
        loading.value[key] = false;
      }
    },
    async getExposureOverview() {
      loading.value.exposureOverview = true;
      errors.value.exposureOverview = undefined;
      exposureOverview.value = undefined;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/exposure_overview",
          {
            body: {
              ...baseParams(),
              exposure_ranges: filters.value.exposure_ranges,
            } as schemas["ReachFrequencyGetExposureOverviewRequest"],
          },
        );
        if (data?.task_id) {
          taskIds.value.exposureOverview = data.task_id;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data.task_id },
          });
        } else {
          loading.value.exposureOverview = false;
        }
      } catch {
        loading.value.exposureOverview = false;
      }
    },
    async getMarketBreakdown(sync = false) {
      if (!exposureMarkets.value?.markets) {
        return undefined;
      }
      const pageSize = exposureMarkets.value?.page_size;
      const pageNumber = exposureMarkets.value?.page_number;
      if (pageNumber === 1) {
        loading.value.marketBreakdown = true;
        errors.value.marketBreakdown = undefined;
      } else {
        loading.value.additionalMarketBreakdown = true;
      }
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const markets = exposureMarkets.value?.markets.slice(
        startIndex,
        endIndex,
      );
      const exposureRanges = !filters.value.market_exposure_ranges
        ? exposureOverview.value?.exposure_ranges
        : filters.value.market_exposure_ranges;
      const params = {
        ...baseParams(),
        markets,
        exposure_ranges: exposureRanges,
        sync,
      };
      delete params.geographies;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/exposure_market_breakdown",
          {
            body: params as schemas["ReachFrequencyGetExposureMarketBreakdownRequest"],
          },
        );
        if (sync) {
          loading.value.additionalMarketBreakdown = false;
          return data?.results;
        } else {
          taskIds.value.marketBreakdown = data?.task_id;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data?.task_id },
          });
        }
      } catch {
        loading.value.marketBreakdown = false;
        loading.value.additionalMarketBreakdown = false;
      }
    },
    async getReachByDay() {
      loading.value.reachByDay = true;
      errors.value.reachByDay = undefined;
      reachByDay.value = undefined;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/reach_and_frequency_by_day",
          {
            body: {
              ...baseParams(),
              frequency: selections.value.frequency,
            } as schemas["ReachFrequencyGetReachAndFrequencyByDayRequest"],
          },
        );
        if (data?.task_id) {
          taskIds.value.reachByDay = data.task_id;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data.task_id },
          });
        } else {
          loading.value.reachByDay = false;
        }
      } catch {
        loading.value.reachByDay = false;
      }
    },
    async getCreativesBreakdown() {
      loading.value.creativesBreakdown = true;
      errors.value.creativesBreakdown = undefined;
      creativesBreakdown.value = undefined;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/creatives_breakdown",
          {
            body: baseParams() as schemas["ReachFrequencyGetCreativesBreakdownRequest"],
          },
        );
        if (data?.task_id) {
          taskIds.value.creativesBreakdown = data.task_id;
          $ws?.subscribe({
            channel: "ReachAndFrequency",
            payload: { id: data.task_id },
          });
        } else {
          loading.value.creativesBreakdown = false;
        }
      } catch {
        loading.value.creativesBreakdown = false;
      }
    },
    handleExportMessage: exportManager.handleMessage,
    clearExportErrors: exportManager.clearErrors,
    cancelExport: exportManager.cancel,
    async export(formattedFilters: any) {
      const payload = {
        metrics: {
          ...reachMetrics.value,
          ...frequencyMetrics.value,
          top_medium: mostEffectiveMedium.value?.medium_name || undefined,
          top_medium_reach: mostEffectiveMedium.value?.reach || undefined,
          top_medium_frequency:
            mostEffectiveMedium.value?.frequency || undefined,
          audience_size: audienceSize.value?.audience_count,
        },
        exposure_overview: {
          exposed_range: exposureOverview.value?.exposure_ranges[2],
          exposure_frequency: exposureOverview.value?.exposure_frequency,
        },
        reach_by_day: {
          ...reachByDay.value,
          extremes: lineGraphExtremes.value,
        },
        market_breakdown: marketBreakdown.value?.slice(0, 3).map((market) => ({
          market_name: market.market_name,
          exposed_range: market.exposure_ranges[2],
          exposure_frequency: market.exposure_frequency,
        })),
        filters: formattedFilters,
      };
      return exportManager.startExport("export", () =>
        client.POST("/api/v1/reach_frequency/export", {
          body: payload as schemas["ReachFrequencyV2ExportRequest"],
        }),
      );
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReachFreqStore, import.meta.hot));
}
